/**
 * Memory Leak Prevention Utility
 * Centralized resource management for preventing memory leaks
 * @author Zubduq Team
 * @version 1.0.0
 */

class MemoryLeakPrevention {
  constructor() {
    this.components = new Map();
    this.globalIntervals = new Set();
    this.globalTimeouts = new Set();
    this.globalEventListeners = new Map();
    this.domReferences = new WeakMap();
    this.observerInstances = new Set();

    // Track global variables to prevent pollution
    this.initialWindowKeys = new Set(Object.keys(window));
    this.addedGlobalKeys = new Set();

    // Performance monitoring
    this.memoryCheckInterval = null;
    this.isMonitoring = false;

    this.init();
  }

  /**
   * Initialize memory leak prevention system
   */
  init() {
    this.overrideGlobalMethods();
    this.startMemoryMonitoring();
    this.setupPageUnloadCleanup();
    console.log('🛡️ Memory Leak Prevention System initialized');
  }

  /**
   * Register a component for lifecycle management
   */
  registerComponent(name, instance) {
    if (this.components.has(name)) {
      console.warn(`⚠️ Component '${name}' already registered. Cleaning up previous instance.`);
      this.unregisterComponent(name);
    }

    const componentData = {
      instance,
      intervals: new Set(),
      timeouts: new Set(),
      eventListeners: new Map(),
      domReferences: new Set(),
      observers: new Set(),
      createdAt: Date.now()
    };

    this.components.set(name, componentData);
    console.log(`📝 Component '${name}' registered for memory management`);
    return componentData;
  }

  /**
   * Unregister and cleanup a component
   */
  unregisterComponent(name) {
    const component = this.components.get(name);
    if (!component) {
      console.debug(`🔍 Component '${name}' not found in memory leak prevention (this is normal if it wasn't registered here)`);
      return false;
    }

    this.cleanupComponent(component);
    this.components.delete(name);
    console.log(`🧹 Component '${name}' unregistered and cleaned up`);
    return true;
  }

  /**
   * Add interval tracking for a component
   */
  addInterval(componentName, intervalId, description = '') {
    const component = this.components.get(componentName);
    if (component) {
      component.intervals.add({ id: intervalId, description, createdAt: Date.now() });
    }
    this.globalIntervals.add(intervalId);
    console.log(`⏰ Interval ${intervalId} tracked for component '${componentName}'${description ? ': ' + description : ''}`);
  }

  /**
   * Add timeout tracking for a component
   */
  addTimeout(componentName, timeoutId, description = '') {
    const component = this.components.get(componentName);
    if (component) {
      component.timeouts.add({ id: timeoutId, description, createdAt: Date.now() });
    }
    this.globalTimeouts.add(timeoutId);
    console.log(`⏱️ Timeout ${timeoutId} tracked for component '${componentName}'${description ? ': ' + description : ''}`);
  }

  /**
   * Add event listener tracking for a component
   */
  addEventListener(componentName, element, event, handler, options = {}) {
    const component = this.components.get(componentName);
    if (component) {
      const listenerKey = `${element.constructor.name}-${event}`;
      if (!component.eventListeners.has(listenerKey)) {
        component.eventListeners.set(listenerKey, []);
      }
      component.eventListeners.get(listenerKey).push({
        element,
        event,
        handler,
        options,
        createdAt: Date.now()
      });
    }
    console.log(`👂 Event listener ${event} tracked for component '${componentName}'`);
  }

  /**
   * Add observer tracking for a component
   */
  addObserver(componentName, observer, description = '') {
    const component = this.components.get(componentName);
    if (component) {
      component.observers.add({ observer, description, createdAt: Date.now() });
    }
    this.observerInstances.add(observer);
    console.log(`👁️ Observer tracked for component '${componentName}'${description ? ': ' + description : ''}`);
  }

  /**
   * Add DOM reference tracking for a component
   */
  addDomReference(componentName, element, description = '') {
    const component = this.components.get(componentName);
    if (component) {
      component.domReferences.add({ element, description, createdAt: Date.now() });
    }
    console.log(`🏠 DOM reference tracked for component '${componentName}'${description ? ': ' + description : ''}`);
  }

  /**
   * Clean up all resources for a component
   */
  cleanupComponent(component) {
    // Clear intervals
    component.intervals.forEach(interval => {
      if (typeof interval === 'object' && interval.id) {
        clearInterval(interval.id);
        this.globalIntervals.delete(interval.id);
      } else {
        clearInterval(interval);
        this.globalIntervals.delete(interval);
      }
    });
    component.intervals.clear();

    // Clear timeouts
    component.timeouts.forEach(timeout => {
      if (typeof timeout === 'object' && timeout.id) {
        clearTimeout(timeout.id);
        this.globalTimeouts.delete(timeout.id);
      } else {
        clearTimeout(timeout);
        this.globalTimeouts.delete(timeout);
      }
    });
    component.timeouts.clear();

    // Remove event listeners
    component.eventListeners.forEach(listeners => {
      listeners.forEach(({ element, event, handler, options }) => {
        try {
          element.removeEventListener(event, handler, options);
        } catch (error) {
          console.warn(`⚠️ Error removing event listener: ${error.message}`);
        }
      });
    });
    component.eventListeners.clear();

    // Disconnect observers
    component.observers.forEach(observerData => {
      const observer = typeof observerData === 'object' ? observerData.observer : observerData;
      try {
        if (observer && typeof observer.disconnect === 'function') {
          observer.disconnect();
          this.observerInstances.delete(observer);
        }
      } catch (error) {
        console.warn(`⚠️ Error disconnecting observer: ${error.message}`);
      }
    });
    component.observers.clear();

    // Clear DOM references
    component.domReferences.forEach(refData => {
      const element = typeof refData === 'object' ? refData.element : refData;
      if (element && typeof element === 'object') {
        // Nullify common properties that might hold references
        element.onclick = null;
        element.onchange = null;
        element.oninput = null;
        element._component = null;
        element._manager = null;
      }
    });
    component.domReferences.clear();

    // Call component's cleanup method if it exists
    if (component.instance && typeof component.instance.cleanup === 'function') {
      try {
        component.instance.cleanup();
      } catch (error) {
        console.warn(`⚠️ Error calling component cleanup method: ${error.message}`);
      }
    }

    console.log(`🧹 Component resources cleaned up successfully`);
  }

  /**
   * Override global methods to track resource creation
   */
  overrideGlobalMethods() {
    // Override setInterval
    const originalSetInterval = window.setInterval;
    window.setInterval = (callback, delay, ...args) => {
      const intervalId = originalSetInterval(callback, delay, ...args);
      this.globalIntervals.add(intervalId);
      console.log(`⏰ Global interval ${intervalId} created (delay: ${delay}ms)`);
      return intervalId;
    };

    // Override setTimeout
    const originalSetTimeout = window.setTimeout;
    window.setTimeout = (callback, delay, ...args) => {
      const timeoutId = originalSetTimeout(callback, delay, ...args);
      this.globalTimeouts.add(timeoutId);
      // console.log(`⏱️ Global timeout ${timeoutId} created (delay: ${delay}ms)`);
      return timeoutId;
    };

    // Override clearInterval
    const originalClearInterval = window.clearInterval;
    window.clearInterval = (intervalId) => {
      this.globalIntervals.delete(intervalId);
      console.log(`⏰ Global interval ${intervalId} cleared`);
      return originalClearInterval(intervalId);
    };

    // Override clearTimeout
    const originalClearTimeout = window.clearTimeout;
    window.clearTimeout = (timeoutId) => {
      this.globalTimeouts.delete(timeoutId);
      // console.log(`⏱️ Global timeout ${timeoutId} cleared`);
      return originalClearTimeout(timeoutId);
    };

    console.log('🔧 Global methods overridden for memory tracking');
  }

  /**
   * Track global variable additions
   */
  trackGlobalVariable(key, value, description = '') {
    if (!this.initialWindowKeys.has(key)) {
      this.addedGlobalKeys.add(key);
      console.log(`🌍 Global variable '${key}' added${description ? ': ' + description : ''}`);
    }
    window[key] = value;
  }

  /**
   * Start memory monitoring
   */
  startMemoryMonitoring() {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.memoryCheckInterval = setInterval(() => {
      this.performMemoryCheck();
    }, 60000); // Check every minute

    console.log('📊 Memory monitoring started');
  }

  /**
   * Stop memory monitoring
   */
  stopMemoryMonitoring() {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
    this.isMonitoring = false;
    console.log('📊 Memory monitoring stopped');
  }

  /**
   * Perform memory check and report potential leaks
   */
  performMemoryCheck() {
    const memoryInfo = performance.memory ? {
      used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
    } : null;

    const report = {
      timestamp: new Date().toISOString(),
      memory: memoryInfo,
      components: this.components.size,
      globalIntervals: this.globalIntervals.size,
      globalTimeouts: this.globalTimeouts.size,
      globalEventListeners: this.globalEventListeners.size,
      observerInstances: this.observerInstances.size,
      addedGlobals: this.addedGlobalKeys.size
    };

    // Warn about potential leaks - adjusted thresholds for modern app complexity
    if (report.globalIntervals > 15) {
      console.warn(`⚠️ High number of active intervals: ${report.globalIntervals}`);
    }
    if (report.globalTimeouts > 50) {
      console.warn(`⚠️ High number of active timeouts: ${report.globalTimeouts}`);
    }
    if (report.observerInstances > 20) {
      console.warn(`⚠️ High number of observer instances: ${report.observerInstances}`);
    }
    if (report.addedGlobals > 50) {
      console.warn(`⚠️ High number of global variables added: ${report.addedGlobals}`);
    }

    console.log('📊 Memory Check:', report);
    return report;
  }

  /**
   * Setup cleanup on page unload
   */
  setupPageUnloadCleanup() {
    window.addEventListener('beforeunload', () => {
      this.cleanupAll();
    });

    window.addEventListener('pagehide', () => {
      this.cleanupAll();
    });

    console.log('🚪 Page unload cleanup handlers registered');
  }

  /**
   * Clean up all tracked resources
   */
  cleanupAll() {
    console.log('🧹 Starting comprehensive cleanup...');

    // Cleanup all registered components
    this.components.forEach((component, name) => {
      this.cleanupComponent(component);
    });
    this.components.clear();

    // Clear remaining global intervals
    this.globalIntervals.forEach(intervalId => clearInterval(intervalId));
    this.globalIntervals.clear();

    // Clear remaining global timeouts
    this.globalTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.globalTimeouts.clear();

    // Disconnect remaining observers
    this.observerInstances.forEach(observer => {
      try {
        if (observer && typeof observer.disconnect === 'function') {
          observer.disconnect();
        }
      } catch (error) {
        console.warn(`⚠️ Error disconnecting observer during cleanup: ${error.message}`);
      }
    });
    this.observerInstances.clear();

    // Stop memory monitoring
    this.stopMemoryMonitoring();

    console.log('✅ Comprehensive cleanup completed');
  }

  /**
   * Get memory leak report
   */
  getMemoryReport() {
    const components = [];
    this.components.forEach((component, name) => {
      components.push({
        name,
        intervals: component.intervals.size,
        timeouts: component.timeouts.size,
        eventListeners: component.eventListeners.size,
        observers: component.observers.size,
        domReferences: component.domReferences.size,
        age: Date.now() - component.createdAt
      });
    });

    return {
      components,
      summary: {
        totalComponents: this.components.size,
        totalIntervals: this.globalIntervals.size,
        totalTimeouts: this.globalTimeouts.size,
        totalObservers: this.observerInstances.size,
        addedGlobals: Array.from(this.addedGlobalKeys)
      }
    };
  }
}

// Create singleton instance
const memoryLeakPrevention = new MemoryLeakPrevention();

// Export for use in other modules
export default memoryLeakPrevention;

// Global access for debugging
window.memoryLeakPrevention = memoryLeakPrevention;

console.log('🛡️ Memory Leak Prevention Utility loaded');
